import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	MenuItem,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import {
	fetchLettingDashboard,
	LettingDashboardParams,
	LettingDashboardResponse,
	LettingDateType,
} from "../../api/dashboard";

export const formatDate = (value: string | null) => (value ? dayjs(value).format("MM/DD/YYYY") : "—");

// avoid refetching the dashboard on every keystroke
export function useDebouncedValue<T>(value: T, delay = 400) {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const id = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(id);
	}, [value, delay]);
	return debounced;
}

export const LettingDashboard: FC = () => {
	const navigate = useNavigate();
	const [dateType, setDateType] = useState<LettingDateType>("letting");
	const [startDate, setStartDate] = useState<Dayjs | null>(null);
	const [endDate, setEndDate] = useState<Dayjs | null>(null);
	const [district, setDistrict] = useState("");
	const [sponsorType, setSponsorType] = useState("");
	const [migrated, setMigrated] = useState<LettingDashboardParams["migrated"]>("");
	const [completed, setCompleted] = useState<LettingDashboardParams["completed"]>("");
	const [searchInput, setSearchInput] = useState("");
	const search = useDebouncedValue(searchInput);
    const [esrFromInput, setEsrFromInput] = useState("");
	const [esrToInput, setEsrToInput] = useState("");
	const esrFrom = useDebouncedValue(esrFromInput);
	const esrTo = useDebouncedValue(esrToInput);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [data, setData] = useState<LettingDashboardResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const invalidRange = !!startDate && !!endDate && endDate.isBefore(startDate, "day");
    const invalidEsrRange = !!esrFrom && !!esrTo && Number(esrTo) < Number(esrFrom);
	// Initial load / nothing chosen: backend shows only the latest non-migrated ESRs
	const isDefaultView =
		!startDate && !endDate && !esrFrom && !esrTo && !district && !sponsorType && !migrated && !completed && !search;

	useEffect(() => {
		if (invalidRange || invalidEsrRange) return;
		setLoading(true);
		setError("");
		fetchLettingDashboard({
			date_type: dateType,
			start: startDate?.format("YYYY-MM-DD"),
			end: endDate?.format("YYYY-MM-DD"),
			district,
			sponsor_type: sponsorType,
			migrated,
			completed,
			search,
            esr_from: esrFrom,
			esr_to: esrTo,
			default_view: isDefaultView,
			page,
			page_size: pageSize,
		})
			.then(setData)
			.catch((e) => {
				console.error(e);
				setError("Unable to load the dashboard.");
			})
			.finally(() => setLoading(false));
	}, [dateType, startDate, endDate, district, sponsorType, migrated, completed, search, esrFrom, esrTo, page, pageSize]);

	// any filter change goes back to the first page
	const withPageReset = <T,>(setter: (value: T) => void) => (value: T) => {
		setter(value);
		setPage(0);
	};

	return (
		<Box>
			<Stack direction="row" gap={2} flexWrap="wrap" alignItems="center" sx={{ mb: 3 }}>
				<ToggleButtonGroup
					exclusive
					size="small"
					color="primary"
					value={dateType}
					onChange={(_, value) => value && withPageReset(setDateType)(value)}
				>
					<ToggleButton value="letting">Letting Date (E26)</ToggleButton>
					<ToggleButton value="completion">Target Completion Date (E24C)</ToggleButton>
				</ToggleButtonGroup>
				<DatePicker
					label="Start date"
					value={startDate}
					onChange={withPageReset(setStartDate)}
					slotProps={{ textField: { size: "small" }, field: { clearable: true } }}
				/>
				<DatePicker
					label="End date"
					value={endDate}
					minDate={startDate ?? undefined}
					onChange={withPageReset(setEndDate)}
					slotProps={{ textField: { size: "small" }, field: { clearable: true } }}
				/>
				<Button
					size="small"
					onClick={() => {
						const today = dayjs();
						setStartDate(today);
						setEndDate(today);
						setPage(0);
					}}
				>
					Today
				</Button>
			</Stack>
			<Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 3 }}>
				<TextField
					select
					size="small"
					label="District"
					value={district}
					onChange={(e) => withPageReset(setDistrict)(e.target.value)}
					sx={{ minWidth: 130 }}
				>
					<MenuItem value="">All</MenuItem>
					{(data?.facets.districts ?? []).map((d) => (
						<MenuItem key={d} value={String(d)}>
							District {d}
						</MenuItem>
					))}
				</TextField>
				<TextField
					select
					size="small"
					label="Sponsor Type"
					value={sponsorType}
					onChange={(e) => withPageReset(setSponsorType)(e.target.value)}
					sx={{ minWidth: 150 }}
				>
					<MenuItem value="">All</MenuItem>
					{(data?.facets.sponsor_types ?? []).map((s) => (
						<MenuItem key={s} value={s}>
							{s}
						</MenuItem>
					))}
				</TextField>
				<TextField
					select
					size="small"
					label="Migrated"
					value={migrated}
					onChange={(e) => withPageReset(setMigrated)(e.target.value as LettingDashboardParams["migrated"])}
					sx={{ minWidth: 130 }}
				>
					<MenuItem value="">All</MenuItem>
					<MenuItem value="yes">Migrated</MenuItem>
					<MenuItem value="no">Non-migrated</MenuItem>
				</TextField>
				{/* <TextField
					select
					size="small"
					label="Status"
					value={completed}
					onChange={(e) => withPageReset(setCompleted)(e.target.value as LettingDashboardParams["completed"])}
					sx={{ minWidth: 180 }}
				>
					<MenuItem value="">All</MenuItem>
					<MenuItem value="yes">Submitted to BDE</MenuItem>
					<MenuItem value="no">Not Submitted to BDE</MenuItem>
				</TextField> */}
                <TextField
					size="small"
					label="ESR # from"
					type="number"
					value={esrFromInput}
					onChange={(e) => withPageReset(setEsrFromInput)(e.target.value)}
					sx={{ width: 130 }}
				/>
				<TextField
					size="small"
					label="ESR # to"
					type="number"
					value={esrToInput}
					onChange={(e) => withPageReset(setEsrToInput)(e.target.value)}
					helperText="Includes addenda of ESRs in range"
					sx={{ width: 130 }}
				/>
				<TextField
					size="small"
					label="Search ESR #"
					value={searchInput}
					onChange={(e) => withPageReset(setSearchInput)(e.target.value)}
				/>
			</Stack>

            {data?.default_view && isDefaultView && (
				<Alert severity="error" sx={{ mb: 2 }}>
					Showing only the latest {data.default_view_limit} non-migrated ESRs. Choose a date range, ESR # range or
					other filters to see more.
				</Alert>
			)}
			{invalidEsrRange && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					ESR # to must be greater than or equal to ESR # from.
				</Alert>
			)}

			{invalidRange && (
				<Alert severity="warning" sx={{ mb: 2 }}>
					End date must be on or after the start date.
				</Alert>
			)}
			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>ESR #</TableCell>
							<TableCell>Migrated</TableCell>
							<TableCell>Letting Date</TableCell>
							<TableCell>Target Completion Date</TableCell>
							<TableCell>ESR Submission Date</TableCell>
							<TableCell>District</TableCell>
							<TableCell>Sponsor Type</TableCell>
							<TableCell>Reviews Required</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell colSpan={9} align="center">
									<CircularProgress size={24} />
								</TableCell>
							</TableRow>
						) : (data?.rows ?? []).length === 0 ? (
							<TableRow>
								<TableCell colSpan={9}>
									<Typography color="text.secondary">No ESRs found for the selected filters.</Typography>
								</TableCell>
							</TableRow>
						) : (
							data?.rows.map((row) => (
								<TableRow key={row.esr_id} hover>
									<TableCell>{row.display_id}</TableCell>
									<TableCell>{row.migrated ? "Yes" : "No"}</TableCell>
									<TableCell>{formatDate(row.letting_date)}</TableCell>
									<TableCell>{formatDate(row.completion_date)}</TableCell>
									<TableCell>
										{row.completed ? (
											formatDate(row.e5_date)
										) : (
											<Typography fontSize={14} color="error">
												Not Submitted to BDE
											</Typography>
										)}
									</TableCell>
									<TableCell>{row.district ?? "—"}</TableCell>
									<TableCell>{row.sponsor_type || "—"}</TableCell>
									<TableCell>
										<Stack direction="row" gap={0.5}>
											{row.units_required.length === 0
												? "—"
												: row.units_required.map((unit) => <Chip key={unit} size="small" label={unit} />)}
										</Stack>
									</TableCell>
									<TableCell>
										<Button size="small" onClick={() => navigate(`/esr/${row.root_id}/${row.label}/${row.esr_id}`)}>
											View
										</Button>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component="div"
				rowsPerPageOptions={[10, 20, 50]}
				count={data?.total ?? 0}
				rowsPerPage={pageSize}
				page={page}
				onPageChange={(_, p) => setPage(p)}
				onRowsPerPageChange={(e) => {
					setPageSize(parseInt(e.target.value, 10));
					setPage(0);
				}}
			/>
		</Box>
	);
};